"use client";

import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ontologyListApiConnections,
  ontologyCreateApiConnection,
  ontologyDeleteApiConnection,
  ontologySyncApiConnection,
  ontologyTestApiConnection,
  ontologyDiscoverApiSchema,
  ontologyGetApiConnection,
  ontologyUpdateApiConnection,
  type ApiConnectionResponse,
  type ApiConnectionWithTablesResponse,
  type CreateApiConnectionRequest,
  type UpdateApiConnectionRequest,
  type TestApiConnectionRequest,
  type TestApiConnectionResponse,
  type DiscoverApiSchemaRequest,
  type DiscoveredSchemaResponse,
  type SyncApiConnectionResponse,
} from "../sdk";
import { GC_TIME, LOW_CHURN_STALE_TIME } from "../query/config";
import { queryKeys } from "../query/keys";

export interface APIConnection {
  id: string;
  connection_name: string;
  description?: string;
  base_url: string;
  endpoint_path: string;
  auth_type: string;
  pagination_type: string;
  sync_mode: string;
  target_namespace: string;
  target_table_name: string;
  last_sync_at?: string;
  last_sync_status?: "success" | "failed" | "running";
  last_sync_records?: number;
  last_sync_error?: string;
  created_at: string;
  updated_at: string;
  created_tables?: { id: string; table_name: string; row_count: number }[];
}

interface APIConnectionsListResponse {
  connections: APIConnection[];
  total: number;
}

interface UseAPIConnectionsResult {
  connections: APIConnection[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseAPIConnectionMutationsResult {
  createConnection: (
    request: CreateApiConnectionRequest,
  ) => Promise<ApiConnectionResponse>;
  updateConnection: (
    connectionId: string,
    request: UpdateApiConnectionRequest,
  ) => Promise<ApiConnectionResponse>;
  deleteConnection: (connectionId: string) => Promise<void>;
  syncConnection: (
    connectionId: string,
    forceFullRefresh?: boolean,
  ) => Promise<SyncApiConnectionResponse>;
  testExistingConnection: (
    connectionId: string,
  ) => Promise<TestApiConnectionResponse>;
  testNewConnection: (
    request: TestApiConnectionRequest,
  ) => Promise<TestApiConnectionResponse>;
  discoverSchema: (
    request: DiscoverApiSchemaRequest,
  ) => Promise<DiscoveredSchemaResponse>;
}

async function fetchApiConnections(
  orgId: string,
): Promise<APIConnectionsListResponse> {
  const response = await ontologyListApiConnections({
    query: { org_id: orgId },
  });
  return {
    connections: (response.data?.connections ?? []) as APIConnection[],
    total: response.data?.total ?? 0,
  };
}

function unwrap<T>(
  response: { data?: T; error?: unknown },
  errorContext: string,
): T {
  if (response.error) {
    const message =
      typeof response.error === "string"
        ? response.error
        : (response.error as { message?: string })?.message ??
          `Failed to ${errorContext}`;
    throw new Error(message);
  }
  if (response.data === undefined || response.data === null) {
    throw new Error(`Failed to ${errorContext}: empty response`);
  }
  return response.data;
}

/**
 * Read hook for the org's API connections. Cached via react-query so
 * revisits to /sources don't refetch and flicker.
 */
export function useAPIConnections(orgId?: string): UseAPIConnectionsResult {
  const query = useQuery({
    queryKey: queryKeys.apiConnections.list({ orgId: orgId ?? "" }),
    queryFn: () => fetchApiConnections(orgId!),
    enabled: !!orgId,
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
  });

  const refetch = useCallback(async () => {
    await query.refetch();
  }, [query]);

  return {
    connections: query.data?.connections ?? [],
    total: query.data?.total ?? 0,
    loading: query.isPending && !!orgId,
    error: query.error instanceof Error ? query.error.message : null,
    refetch,
  };
}

/**
 * Mutation bundle for API connections. Each mutation invalidates the
 * list query on success so the next render shows fresh data.
 */
export function useAPIConnectionMutations(
  orgId?: string,
): UseAPIConnectionMutationsResult {
  const queryClient = useQueryClient();

  const invalidateList = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.apiConnections.list({ orgId: orgId ?? "" }),
    });
  }, [queryClient, orgId]);

  const createConnection = useCallback(
    async (request: CreateApiConnectionRequest) => {
      if (!orgId) {
        throw new Error(
          "Organization ID is required to create an API connection",
        );
      }
      const response = await ontologyCreateApiConnection({
        body: request,
        query: { org_id: orgId },
      });
      const data = unwrap(response, "create API connection") as ApiConnectionResponse;
      if (!data.id) {
        throw new Error("API connection creation returned no id");
      }
      invalidateList();
      return data;
    },
    [invalidateList, orgId],
  );

  const updateConnection = useCallback(
    async (connectionId: string, request: UpdateApiConnectionRequest) => {
      if (!orgId) {
        throw new Error(
          "Organization ID is required to update an API connection",
        );
      }
      const response = await ontologyUpdateApiConnection({
        path: { connection_id: connectionId },
        body: request,
        query: { org_id: orgId },
      });
      const data = unwrap(response, "update API connection") as ApiConnectionResponse;
      invalidateList();
      return data;
    },
    [invalidateList, orgId],
  );

  const deleteConnection = useCallback(
    async (connectionId: string) => {
      if (!orgId) {
        throw new Error(
          "Organization ID is required to delete an API connection",
        );
      }
      await ontologyDeleteApiConnection({
        path: { connection_id: connectionId },
        query: { org_id: orgId },
      });
      invalidateList();
    },
    [invalidateList, orgId],
  );

  const syncConnection = useCallback(
    async (connectionId: string, forceFullRefresh: boolean = false) => {
      if (!orgId) {
        throw new Error(
          "Organization ID is required to sync an API connection",
        );
      }
      const response = await ontologySyncApiConnection({
        path: { connection_id: connectionId },
        body: { force_full_refresh: forceFullRefresh },
        query: { org_id: orgId },
      });
      const data = unwrap(response, "sync API connection") as SyncApiConnectionResponse;
      // Sync result lands later via polling; invalidate so the next
      // refetch picks up status changes.
      invalidateList();
      return data;
    },
    [invalidateList, orgId],
  );

  const testExistingConnection = useCallback(
    async (connectionId: string) => {
      if (!orgId) {
        throw new Error(
          "Organization ID is required to test an API connection",
        );
      }
      // Get connection details first to build test request.
      const connResponse = await ontologyGetApiConnection({
        path: { connection_id: connectionId },
        query: { org_id: orgId },
      });
      const conn = unwrap(connResponse, "load API connection") as ApiConnectionWithTablesResponse;
      const testRequest: TestApiConnectionRequest = {
        base_url: conn.base_url,
        endpoint_path: conn.endpoint_path,
        http_method: conn.http_method || "GET",
        headers: conn.default_headers,
        query_params: conn.query_params,
        data_path: conn.data_path,
        auth_type: conn.auth_type,
      };
      const response = await ontologyTestApiConnection({ body: testRequest });
      return unwrap(response, "test API connection") as TestApiConnectionResponse;
    },
    [orgId],
  );

  const testNewConnection = useCallback(
    async (request: TestApiConnectionRequest) => {
      const response = await ontologyTestApiConnection({ body: request });
      return unwrap(response, "test API connection") as TestApiConnectionResponse;
    },
    [],
  );

  const discoverSchema = useCallback(
    async (request: DiscoverApiSchemaRequest) => {
      const response = await ontologyDiscoverApiSchema({ body: request });
      return unwrap(response, "discover API schema") as DiscoveredSchemaResponse;
    },
    [],
  );

  return {
    createConnection,
    updateConnection,
    deleteConnection,
    syncConnection,
    testExistingConnection,
    testNewConnection,
    discoverSchema,
  };
}

/**
 * Hook for getting a single API connection by ID.
 */
export function useAPIConnection(orgId?: string, connectionId?: string | null) {
  const [connection, setConnection] =
    useState<ApiConnectionWithTablesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConnection = useCallback(async () => {
    if (!orgId || !connectionId) {
      setConnection(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await ontologyGetApiConnection({
        path: { connection_id: connectionId },
        query: { org_id: orgId },
      });

      setConnection(response.data as ApiConnectionWithTablesResponse);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch API connection",
      );
      console.error("🔴 Error fetching API connection:", err);
    } finally {
      setLoading(false);
    }
  }, [orgId, connectionId]);

  const refetch = useCallback(async () => {
    await fetchConnection();
  }, [fetchConnection]);

  useEffect(() => {
    if (orgId && connectionId) {
      fetchConnection();
    }
  }, [fetchConnection, orgId, connectionId]);

  return {
    connection,
    loading,
    error,
    refetch,
  };
}
